import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'
import AssetDownload from '@comp/product/asset-download'

interface PageContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  primaryCta: string
  secondaryCta: string
  badges: string[]
  pillarsEyebrow: string
  pillarsTitle: string
  pillarsBody: string
  pillars: Pillar[]
  howEyebrow: string
  howTitle: string
  steps: Step[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    metaTitle: 'Machine identity management',
    metaDescription:
      'Discover every TLS certificate, SSH key, and service account, automate issuance and renewal with ACME, AD CS, or a built-in private CA, vault and rotate non-human credentials, and stay ahead of shrinking certificate lifetimes.',
    heroEyebrow: 'Use cases · Machine identity',
    heroTitle: [
      'Your machines outnumber your people.',
      'Manage their identities like it.',
    ],
    heroBody:
      'TLS certificates, SSH keys, service accounts, and workload credentials outnumber your human identities many times over, and most of them live untracked. With public TLS lifetimes shrinking stepwise toward 47 days by 2029, manual renewal stops being an option: a single expired certificate is enough to take down a customer-facing service. Monopam finds them, renews them, and rotates them.',
    primaryCta: 'Start free trial →',
    secondaryCta: 'Talk with our experts',
    badges: [
      'Discovery across network and servers',
      'ACME, AD CS, built-in private CA',
      'Automatic rotation for non-human credentials',
    ],
    pillarsEyebrow: 'What you get',
    pillarsTitle: 'From unknown inventory to automated lifecycle.',
    pillarsBody:
      'You cannot renew what you have never found. Monofor starts with discovery, then puts every certificate and credential on a lifecycle that runs without a spreadsheet.',
    pillars: [
      {
        title: 'Discover every certificate and key',
        description:
          'Network scans and agentless discovery sweep the estate and merge everything they find into one inventory you can trust.',
        bullets: [
          'Network TLS scanning across ranges and ports',
          'Server and Windows certificate store discovery via the agentless gateway',
          'Thumbprint-deduplicated single inventory',
          'Scheduled re-scans keep the picture current',
        ],
      },
      {
        title: 'Automate issuance and renewal',
        description:
          'Certificates come from the CA you already trust and renew before anyone has to remember, with an approval gate wherever policy demands one.',
        bullets: [
          'ACME with Let’s Encrypt or any ACMEv2 CA',
          'Microsoft AD CS and a built-in private CA',
          'Approval-gated issuance where policy requires it',
          'Renew-then-deploy to nginx, Apache, and IIS',
        ],
      },
      {
        title: 'Vault and rotate the non-human accounts',
        description:
          'Machine identity is more than certificates: the service accounts and keys behind your workloads move into the vault and rotate on their own.',
        bullets: [
          'Service accounts, SSH keys, API keys, and database credentials',
          'Vaulted with automatic rotation',
          'Brokered checkout for people and pipelines',
          'Credentials injected, never exposed in plaintext',
        ],
      },
      {
        title: 'Stay ahead of crypto risk',
        description:
          'Shorter lifetimes are only half the story. NIST standardized the first post-quantum algorithms in 2024; knowing where your cryptography stands is now part of the job.',
        bullets: [
          'Expiry digests before certificates become incidents',
          'TLS protocol probing flags weak configurations',
          'CT-log monitoring for certificates you did not issue',
          'Crypto-agility and post-quantum readiness reporting',
        ],
      },
    ],
    howEyebrow: 'How it works',
    howTitle: 'Find them. Renew them. Rotate them.',
    steps: [
      {
        title: 'Scan',
        description:
          'Run network and server discovery to build one deduplicated inventory of every certificate and key, including the ones nobody remembered.',
      },
      {
        title: 'Automate',
        description:
          'Connect ACME, AD CS, or the built-in CA and let renewal and deployment run on policy, with approvals exactly where you want them.',
      },
      {
        title: 'Rotate',
        description:
          'Vault service accounts, SSH keys, and API credentials and rotate them automatically; checkout is brokered, and plaintext never leaves the vault.',
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'The capabilities behind this use case.',
    related: [
      {
        label: 'Certificate Manager',
        description: 'Discovery, issuance, renewal, and deployment in one place.',
        href: '/pam/certificate-manager',
      },
      {
        label: 'Credential vault',
        description: 'Vaulted secrets with rotation and brokered checkout.',
        href: '/pam/vault',
      },
      {
        label: 'Blog: 47-day certificates',
        description: 'Why TLS lifetimes are shrinking, and what it means for you.',
        href: '/blog/tls-certificate-lifetimes-are-shrinking-to-47-days',
      },
    ],
  },
  tr: {
    metaTitle: 'Makine kimliği yönetimi',
    metaDescription:
      'Her TLS sertifikasını, SSH anahtarını ve servis hesabını keşfedin; ACME, AD CS veya yerleşik özel CA ile sertifika üretimini ve yenilemeyi otomatikleştirin; insan dışı kimlik bilgilerini kasalayıp rotasyona alın ve kısalan sertifika ömürlerinin önünde kalın.',
    heroEyebrow: 'Kullanım senaryoları · Makine kimliği',
    heroTitle: [
      'Makineleriniz insanlarınızdan kalabalık.',
      'Kimliklerini de buna göre yönetin.',
    ],
    heroBody:
      'TLS sertifikaları, SSH anahtarları, servis hesapları ve workload kimlik bilgileri insan kimliklerinizin kat kat üzerinde ve çoğu hiçbir envanterde kayıtlı değil. Genel TLS sertifika ömürleri kademeli olarak 2029’da 47 güne inerken elle yenileme bir seçenek olmaktan çıkıyor: müşteriye açık bir servisi düşürmek için süresi geçmiş tek bir sertifika yeter. Monopam bu kimlikleri bulur, yeniler ve rotasyona alır.',
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Uzmanlarımızla görüşün',
    badges: [
      'Ağda ve sunucularda keşif',
      'ACME, AD CS, yerleşik özel CA',
      'İnsan dışı kimlik bilgilerinde otomatik rotasyon',
    ],
    pillarsEyebrow: 'Ne elde edersiniz',
    pillarsTitle: 'Bilinmeyen envanterden otomatik yaşam döngüsüne.',
    pillarsBody:
      'Hiç bulamadığınız sertifikayı yenileyemezsiniz. Monofor işe keşifle başlar; sonra her sertifikayı ve kimlik bilgisini elektronik tablo gerektirmeyen bir yaşam döngüsüne oturtur.',
    pillars: [
      {
        title: 'Her sertifikayı ve anahtarı keşfedin',
        description:
          'Ağ taramaları ve ajansız keşif ortamı baştan sona tarar; bulunan her şey güvenebileceğiniz tek bir envanterde birleşir.',
        bullets: [
          'Aralık ve port bazında ağ TLS taraması',
          'Ajansız gateway ile sunucu ve Windows sertifika deposu keşfi',
          'Thumbprint ile tekilleştirilmiş tek envanter',
          'Zamanlanmış yeniden taramalarla her zaman güncel resim',
        ],
      },
      {
        title: 'Üretimi ve yenilemeyi otomatikleştirin',
        description:
          'Sertifikalar zaten güvendiğiniz CA’dan gelir ve kimsenin hatırlaması gerekmeden yenilenir, politika gerektirdiği her noktada onay kapısıyla.',
        bullets: [
          'Let’s Encrypt veya herhangi bir ACMEv2 CA ile ACME',
          'Microsoft AD CS ve yerleşik özel CA',
          'Politika gerektirdiğinde onay kapılı sertifika üretimi',
          'Yenile ve dağıt: nginx, Apache ve IIS’e otomatik kurulum',
        ],
      },
      {
        title: 'İnsan dışı hesapları kasalayın ve döndürün',
        description:
          'Makine kimliği sertifikadan ibaret değil: workload’larınızın arkasındaki servis hesapları ve anahtarlar kasaya taşınır ve kendi kendine döner.',
        bullets: [
          'Servis hesapları, SSH anahtarları, API anahtarları ve veritabanı kimlik bilgileri',
          'Kasada, otomatik rotasyonla',
          'İnsanlar ve pipeline’lar için aracılı checkout',
          'Kimlik bilgisi enjekte edilir, düz metin olarak asla görünmez',
        ],
      },
      {
        title: 'Kripto riskinin önünde kalın',
        description:
          'Kısalan ömürler hikâyenin yalnızca yarısı. NIST ilk post-quantum algoritmalarını 2024’te standartlaştırdı; kriptografinizin nerede durduğunu bilmek artık işin bir parçası.',
        bullets: [
          'Sertifikalar olaya dönüşmeden önce vade özetleri',
          'TLS protokol sondasıyla zayıf yapılandırma tespiti',
          'Sizin üretmediğiniz sertifikalar için CT-log izleme',
          'Kripto çevikliği ve post-quantum hazırlık raporlaması',
        ],
      },
    ],
    howEyebrow: 'Nasıl çalışır',
    howTitle: 'Bulun. Yenileyin. Rotasyona alın.',
    steps: [
      {
        title: 'Tarayın',
        description:
          'Ağ ve sunucu keşfini çalıştırın; kimsenin hatırlamadıkları dahil her sertifika ve anahtar, tekilleştirilmiş tek envanterde toplansın.',
      },
      {
        title: 'Otomatikleştirin',
        description:
          'ACME’yi, AD CS’i veya yerleşik CA’yı bağlayın; yenileme ve dağıtım politikayla, tam istediğiniz noktalarda onayla çalışsın.',
      },
      {
        title: 'Rotasyona alın',
        description:
          'Servis hesaplarını, SSH ve API anahtarlarını kasaya alın ve otomatik döndürün; checkout aracılıdır, düz metin kasadan çıkmaz.',
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'Bu senaryonun arkasındaki yetenekler.',
    related: [
      {
        label: 'Certificate Manager',
        description: 'Keşif, üretim, yenileme ve dağıtım tek yerde.',
        href: '/pam/certificate-manager',
      },
      {
        label: 'Kimlik bilgisi kasası',
        description: 'Rotasyon ve aracılı checkout ile kasalanmış sırlar.',
        href: '/pam/vault',
      },
      {
        label: 'Blog: 47 güne inen sertifikalar',
        description: 'TLS ömürleri neden kısalıyor ve sizin için ne anlama geliyor.',
        href: '/blog/ssl-sertifika-omurleri-47-gune-iniyor',
      },
    ],
  },
}

type Props = { locale: string }

export default function MachineIdentityPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/solutions/machine-identity"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: '/contact' }}
          badges={c.badges}
        />
        <SolutionPillars
          eyebrow={c.pillarsEyebrow}
          title={<>{c.pillarsTitle}</>}
          body={c.pillarsBody}
          pillars={c.pillars}
        />
        <ProductHowItWorks
          eyebrow={c.howEyebrow}
          title={<>{c.howTitle}</>}
          steps={c.steps}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <AssetDownload
          asset="47-day-checklist"
          title={{
            en: 'How ready are you for 47-day certificates?',
            tr: '47 günlük sertifikalara ne kadar hazırsınız?',
          }}
          copy={{
            en: 'A 17-point Yes/Partly/No checklist across visibility, automation, risk, and ownership: find the section where your next outage is scheduled.',
            tr: 'Görünürlük, otomasyon, risk ve sahiplik başlıklarında 17 maddelik Evet/Kısmen/Hayır listesi: bir sonraki kesintinin hangi bölümde saklandığını bulun.',
          }}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
